// リソースの変更（CPU・メモリ・データディスク）と、時刻指定のスケジュール（定期再起動・コマンド実行・定期スナップショット）。
// Web の型は apps/web/src/api/types-maint.ts と同じ形にする。
import { z } from 'zod';

/** リソースの変更。指定した項目だけ変える。データディスクは拡大のみ（停止不要）、CPU・メモリは VM の再起動を伴う */
export const ChangeResourcesParamsSchema = z
  .object({
    cores: z.number().int().min(1).max(32).optional(),
    memoryMb: z.number().int().min(1024).max(65536).optional(),
    heapMinMb: z
      .number()
      .int()
      .min(512)
      .max(61440)
      .describe('Minimum Java heap (-Xms), in MB; must not exceed heapMb. Omitted fields remain unchanged.')
      .optional(),
    heapMb: z
      .number()
      .int()
      .min(512)
      .max(61440)
      .describe('Maximum Java heap (-Xmx), in MB; must fit VM memory and be at least heapMinMb.')
      .optional(),
    dataDiskGb: z.number().int().min(10).max(4096).optional(),
  })
  .strict()
  .refine((p) => Object.values(p).some((v) => v !== undefined), '変更する項目がありません');
export type ChangeResourcesParams = z.infer<typeof ChangeResourcesParamsSchema>;

const Time = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/);
const Days = z
  .array(z.number().int().min(0).max(6))
  .max(7)
  .refine((d) => new Set(d).size === d.length, '曜日が重複しています')
  .default([]);
/** コマンド 1 行。改行・NUL は不可。先頭の / は付けない */
export const ScheduledCommandSchema = z
  .string()
  .trim()
  .min(1)
  .max(500)
  .refine((c) => !/[\r\n\0]/.test(c), '改行は使えません')
  .transform((c) => c.replace(/^\//, ''));

/** 定期スナップショットを残せる数の上限。Proxmox のスナップショットはディスクの差分を持ち続けるので、多くしすぎない */
export const MAX_SCHEDULED_SNAPSHOTS = 30;
export const ScheduledTaskKindSchema = z.enum(['restart', 'command', 'snapshot']);
export type ScheduledTaskKind = z.infer<typeof ScheduledTaskKindSchema>;

export const ScheduledTaskInputSchema = z
  .object({
    name: z.string().trim().min(1).max(60),
    enabled: z.boolean(),
    kind: ScheduledTaskKindSchema,
    /** Asia/Tokyo の "HH:MM" */
    time: Time,
    /** 0=日〜6=土。空なら毎日 */
    days: Days,
    /** kind=command のときに順に実行する */
    commands: z.array(ScheduledCommandSchema).max(20).default([]),
    /** kind=restart のとき、何分前にゲーム内で予告するか */
    warnMinutes: z
      .array(z.number().int().min(1).max(60))
      .max(5)
      .refine((w) => new Set(w).size === w.length, '予告の時間が重複しています')
      .transform((w) => [...w].sort((a, b) => b - a))
      .default([10, 5, 1]),
    /** kind=restart のとき、接続中のプレイヤーがいたらその回は見送る */
    skipIfPlayersOnline: z.boolean().default(false),
    /** kind=snapshot のとき、このスケジュールで取ったものを何個まで残すか（古いものから消す） */
    keep: z.number().int().min(1).max(MAX_SCHEDULED_SNAPSHOTS).default(7),
  })
  .strict()
  .refine((t) => t.kind !== 'command' || t.commands.length > 0, 'コマンドを 1 つ以上指定してください');
export type ScheduledTaskInput = z.infer<typeof ScheduledTaskInputSchema>;

export const TaskRunResultSchema = z.enum([
  'restarted',
  'executed',
  'snapshotted',
  'skipped_players',
  'skipped_busy',
  'skipped_stopped',
  'failed',
]);
export type TaskRunResult = z.infer<typeof TaskRunResultSchema>;
export const LastRunSchema = z.object({ at: z.string(), result: TaskRunResultSchema, message: z.string().optional() });

export const ScheduledTaskSchema = z.object({
  id: z.string(),
  serverId: z.string(),
  name: z.string(),
  enabled: z.boolean(),
  kind: ScheduledTaskKindSchema,
  time: Time,
  days: z.array(z.number().int()),
  commands: z.array(z.string()),
  warnMinutes: z.array(z.number().int()),
  skipIfPlayersOnline: z.boolean(),
  keep: z.number().int(),
  lastRun: LastRunSchema.optional(),
  nextRunAt: z.string().optional(),
});
export type ScheduledTask = z.infer<typeof ScheduledTaskSchema>;

/** 時刻と曜日だけを見る部分（nextRunAt / scheduleAction が使う） */
export interface Timing {
  enabled: boolean;
  time: string;
  days: number[];
  warnMinutes: number[];
}

/** 予定は Asia/Tokyo（UTC+9、夏時間なし）で解釈する */
const JST_OFFSET = 9 * 3_600_000;

/** now 以降で最初の予定時刻（UTC の Date）。無効なら undefined */
export function nextRunAt(schedule: Timing, now: Date): Date | undefined {
  if (!schedule.enabled) return undefined;
  const [hour, minute] = schedule.time.split(':').map(Number) as [number, number];
  const local = new Date(now.getTime() + JST_OFFSET);
  for (let offset = 0; offset <= 7; offset += 1) {
    const day = new Date(
      Date.UTC(local.getUTCFullYear(), local.getUTCMonth(), local.getUTCDate() + offset, hour, minute),
    );
    const at = new Date(day.getTime() - JST_OFFSET);
    if (at.getTime() <= now.getTime()) continue;
    if (schedule.days.length && !schedule.days.includes(day.getUTCDay())) continue;
    return at;
  }
  return undefined;
}

export type ScheduleAction =
  | { kind: 'none' }
  /** covers: 送信済みとして記録する予告（一度に複数を切ったとき、遠いほうは送らずに済ませる） */
  | { kind: 'warn'; minutes: number; covers: number[]; at: Date }
  | { kind: 'run'; at: Date };

/**
 * いま何をすべきかを決める（30 秒ごとに呼ぶ）。
 * sent: その予定時刻に対して送信済みの予告（分）と、実行済みかどうか。
 * 予定時刻から graceMs を過ぎた分は取り逃がしとして何もしない。
 */
export function scheduleAction(
  schedule: Timing,
  now: Date,
  sent: { at?: string; warned: number[]; done: boolean },
  graceMs = 10 * 60_000,
): ScheduleAction {
  if (!schedule.enabled) return { kind: 'none' };
  // 直前の予定（いまから 1 日以内に過ぎたもの）か、次の予定を対象にする
  const previous = nextRunAt(schedule, new Date(now.getTime() - graceMs));
  const target = previous && previous.getTime() <= now.getTime() ? previous : nextRunAt(schedule, now);
  if (!target) return { kind: 'none' };
  const sameTarget = sent.at === target.toISOString();
  const remaining = target.getTime() - now.getTime();
  if (remaining <= 0) {
    if (sameTarget && sent.done) return { kind: 'none' };
    return remaining >= -graceMs ? { kind: 'run', at: target } : { kind: 'none' };
  }
  const minutesLeft = remaining / 60_000;
  // 予告は「その分数を切ったとき」に一度だけ。複数を一度に切った場合は一番近いものだけ送る
  const due = schedule.warnMinutes.filter((m) => minutesLeft <= m && !(sameTarget && sent.warned.includes(m)));
  if (!due.length) return { kind: 'none' };
  return { kind: 'warn', minutes: Math.min(...due), covers: due, at: target };
}

const agentPolicyShape = {
  /** VM が起動したとき、最後に起動していたワールドを自動で起動する */
  autoStart: z.boolean().default(true),
  /** クラッシュしたら自動で再起動する */
  autoRestart: z.boolean().default(true),
  /** windowMinutes 分の間にこの回数を超えてクラッシュしたら、それ以上は再起動しない */
  maxRestarts: z.number().int().min(1).max(10).default(3),
  windowMinutes: z.number().int().min(1).max(120).default(10),
};
/** agent に渡す自動化の方針。1.40.0 までの agent も同じ形を strict に受け取るので、項目を足さない */
export const AgentPolicySchema = z.object(agentPolicyShape).strict();
export type AgentPolicy = z.infer<typeof AgentPolicySchema>;

/** サーバーごとの自動化の方針（Panel が保存する。agent には agentPolicy で渡す） */
export const ServerPolicySchema = z
  .object({
    ...agentPolicyShape,
    /** Minecraft を止めたままこの分数がたったら、Panel が VM もシャットダウンしてメモリを空ける。0 で止めない */
    idleVmShutdownMinutes: z.number().int().min(0).max(1440).default(30),
  })
  .strict();
export type ServerPolicy = z.infer<typeof ServerPolicySchema>;

/** agent に渡す分。Panel だけが使う項目を外す */
export function agentPolicy(policy: ServerPolicy): AgentPolicy {
  const { idleVmShutdownMinutes: _panelOnly, ...rest } = policy;
  return rest;
}
